import React from 'react';
import { Form } from 'react-final-form';
import { Localizer } from 'services/Localizer';
import { ServerData } from 'services/ServerData';
import Spinner from 'shared/components/Spinner';
import XSVG from 'shared/components/svg/XSVG';
import { DropdownField, StarRatingField, TextAreaField, TextBoxField } from 'shared/edit/fields';
import ReCaptchaDisclaimer from 'shared/edit/fields/ReCaptchaDisclaimer';
import InlineAlert from 'shared/edit/InlineAlert';
import { FormValidationPromise } from 'shared/types';
import { ReviewPostDto } from './types';

/**
 * Write Review Component Props
 */
interface WriteReviewProps {
	saveReview: (formValues: ReviewPostDto) => FormValidationPromise<void>;
	setShowWriteReview: (prop: boolean) => void;
	hasValidationError: boolean;
	isSaving: boolean;
	productId: string;
	loggedInUserFirstName: string;
}

/**
 * Write Review Component
 * @param props
 */
export default function WriteReview(props: WriteReviewProps) {
	const { saveReview, hasValidationError, isSaving, setShowWriteReview, productId, loggedInUserFirstName } = props;

	return (
		<Form
			onSubmit={saveReview}
			subscription={{ pristine: true, dirty: true, submitError: true }}
			initialValues={{
				reviewerName: loggedInUserFirstName,
				productId,
				rating: null,
				isProductRecommended: true,
				description: '',
				location: '',
				reCaptchaKey: '',
				reCaptchaToken: '',
				title: '',
				id: '',
				localizedReviewDate: '',
			}}
			render={({ handleSubmit }) => {
				return (
					<form onSubmit={handleSubmit} className="roc-pdp-review-form">
						<div className="roc-pdp-review-form__close">
							<button
								className="roc-pdp-review-form__close-btn"
								onClick={() => setShowWriteReview(false)}
								type="button"
							>
								<span className="roc-pdp-review-form__close-text">{Localizer('Close')}</span>
								<span className="roc-pdp-review-form__close-icon">
									<XSVG />
								</span>
							</button>
						</div>

						<div className="roc-pdp-review-form__body">
							<div className="roc-pdp-review-form__body-head">
								<h2 className="roc-pdp-review-form__title">{Localizer('WriteAReview')}</h2>
								<p className="roc-pdp-review-form__subtitle">{Localizer('RequiredFields')}</p>
							</div>
							{hasValidationError ? (
								<InlineAlert
									title={Localizer('ErrorMessageGenericTitle')}
									message={Localizer('CheckEnteredValues')}
									error={null}
								/>
							) : null}
							<div className="roc-pdp-review-form__stars">
								<div className="roc-form-group">
									<StarRatingField label={Localizer('Rating')} name="rating" isRequired />
								</div>
							</div>

							<TextBoxField label={Localizer('ReviewerName')} name="reviewerName" isRequired />

							<div className="roc-row">
								<div className="roc-col-sm-6">
									<TextBoxField isRequired={false} label={Localizer('Location')} name="location" />
								</div>
								<div className="roc-col-sm-6">
									<DropdownField
										label={Localizer('WouldYouRecommend')}
										name="isProductRecommended"
										options={[
											{ value: 'true', text: Localizer('Yes') },
											{ value: 'false', text: Localizer('No') },
										]}
										addSelectOption={false}
										isRequired
									/>
								</div>
							</div>

							<TextBoxField label={Localizer('Summary')} name="summary" isRequired />

							<TextAreaField label={Localizer('Review')} name="review" isRequired />
						</div>
						<ReCaptchaDisclaimer isEnabled={ServerData.IsSubmitReviewReCaptchaEnabled} />
						<div className="roc-pdp-review-form__footer">
							<button
								type="button"
								color="primary-alt"
								className="roc-btn roc-pdp-review-form__btn roc-pdp-review-form__cancel-btn"
								onClick={() => {
									setShowWriteReview(false);
								}}
							>
								{Localizer('Cancel')}
							</button>{' '}
							<button
								type="submit"
								color="secondary"
								className="roc-btn roc-pdp-review-form__btn roc-pdp-review-form__submit-btn"
								disabled={isSaving}
								data-testid="write-review-submit-input-button"
							>
								{isSaving ? <Spinner /> : Localizer('Submit')}
							</button>
						</div>
					</form>
				);
			}}
		/>
	);
}
